/**
 * Consultas públicas e cálculo de re-aceite dos documentos legais.
 *
 * A camada de dados mantém a regra fora das telas: a UI não deve decidir se uma revisão está
 * vigente nem inferir que trocar o locale exige novo consentimento. A RLS continua sendo a
 * fronteira de segurança; este módulo apenas valida a forma da resposta e aplica a política de
 * seleção no cliente.
 */

export const LEGAL_LOCALES = ['pt-PT', 'en', 'es', 'fr'] as const;
export type LegalLocale = (typeof LEGAL_LOCALES)[number];

export const LEGAL_DOCUMENT_KINDS = [
  'terms_consumer',
  'terms_business',
  'privacy_policy',
  'cookie_policy',
  'ranking_transparency',
  'accessibility_statement',
] as const;
export type LegalDocumentKind = (typeof LEGAL_DOCUMENT_KINDS)[number];

export type LegalRole =
  | 'consumer'
  | 'professional_owner'
  | 'professional_employee'
  | 'customer_success'
  | 'admin'
  | 'system';

export type SignupRoleIntent = 'consumer' | 'professional_owner';

export interface LegalDocument {
  id: string;
  kind: LegalDocumentKind;
  version: string;
  locale: LegalLocale;
  title: string;
  body_md: string;
  summary_of_changes: string | null;
  published_at: string | null;
  effective_from: string;
  superseded_at: string | null;
  created_at: string;
}

export interface LegalAcceptanceReference {
  document_id: string;
  legal_documents?: Pick<LegalDocument, 'kind' | 'version' | 'locale'> | null;
}

export type LegalAcceptanceMethod = 'checkbox' | 'signup' | 'reacceptance_prompt';

export interface LegalAcceptance {
  id: string;
  document_id: string;
  accepted_at: string;
  method: LegalAcceptanceMethod;
}

export interface LegalClient {
  from(table: string): {
    // O SDK do Supabase tem builders genéricos profundos. O contrato desta camada é só o
    // encadeamento de filtros e o resultado final; manter o adapter estreito evita instanciar
    // milhares de tipos do PostgREST nos testes e no typecheck.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    select(columns: string): any;
  };
  functions?: {
    invoke(
      name: string,
      options: { body: Record<string, unknown> },
    ): Promise<{ data: unknown; error: { message: string } | null }>;
  };
}

interface QueryResult<T> {
  data: T[] | null;
  error: { message: string } | null;
}

export class LegalDataError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'LegalDataError';
  }
}

const DOCUMENT_COLUMNS = [
  'id',
  'kind',
  'version',
  'locale',
  'title',
  'body_md',
  'summary_of_changes',
  'published_at',
  'effective_from',
  'superseded_at',
  'created_at',
].join(', ');

const LOCALE_SET = new Set<string>(LEGAL_LOCALES);
const KIND_SET = new Set<string>(LEGAL_DOCUMENT_KINDS);
const ACCEPTANCE_METHOD_SET = new Set<LegalAcceptanceMethod>([
  'checkbox',
  'signup',
  'reacceptance_prompt',
]);

function assertLocale(locale: string): asserts locale is LegalLocale {
  if (!LOCALE_SET.has(locale)) throw new LegalDataError('locale_invalido');
}

function assertKinds(kinds: readonly string[] | undefined): void {
  if (!kinds) return;
  if (kinds.length === 0 || kinds.some((kind) => !KIND_SET.has(kind))) {
    throw new LegalDataError('kind_invalido');
  }
}

function parseDocument(raw: unknown): LegalDocument {
  if (typeof raw !== 'object' || raw === null) throw new LegalDataError('legal_document_invalido');

  const value = raw as Record<string, unknown>;
  const fields = ['id', 'version', 'title', 'body_md', 'effective_from', 'created_at'];
  if (fields.some((field) => typeof value[field] !== 'string' || value[field] === '')) {
    throw new LegalDataError('legal_document_invalido');
  }
  if (!KIND_SET.has(String(value.kind)) || !LOCALE_SET.has(String(value.locale))) {
    throw new LegalDataError('legal_document_invalido');
  }

  for (const field of ['published_at', 'superseded_at', 'summary_of_changes']) {
    if (value[field] !== null && value[field] !== undefined && typeof value[field] !== 'string') {
      throw new LegalDataError('legal_document_invalido');
    }
  }

  return {
    id: value.id as string,
    kind: value.kind as LegalDocumentKind,
    version: value.version as string,
    locale: value.locale as LegalLocale,
    title: value.title as string,
    body_md: value.body_md as string,
    summary_of_changes: (value.summary_of_changes as string | null | undefined) ?? null,
    published_at: (value.published_at as string | null | undefined) ?? null,
    effective_from: value.effective_from as string,
    superseded_at: (value.superseded_at as string | null | undefined) ?? null,
    created_at: value.created_at as string,
  };
}

function parseAcceptance(raw: unknown): LegalAcceptance {
  if (typeof raw !== 'object' || raw === null) throw new LegalDataError('legal_acceptance_invalido');

  const value = raw as Record<string, unknown>;
  if (
    typeof value.id !== 'string'
    || value.id === ''
    || typeof value.document_id !== 'string'
    || value.document_id === ''
    || typeof value.accepted_at !== 'string'
    || value.accepted_at === ''
    || !ACCEPTANCE_METHOD_SET.has(value.method as LegalAcceptanceMethod)
  ) {
    throw new LegalDataError('legal_acceptance_invalido');
  }

  return {
    id: value.id,
    document_id: value.document_id,
    accepted_at: value.accepted_at,
    method: value.method as LegalAcceptanceMethod,
  };
}

function isActive(document: LegalDocument, now: Date): boolean {
  return (
    document.published_at !== null
    && document.superseded_at === null
    && new Date(document.effective_from).getTime() <= now.getTime()
  );
}

function newestPerKind(documents: LegalDocument[]): LegalDocument[] {
  const byKind = new Map<LegalDocumentKind, LegalDocument>();
  for (const document of documents) {
    const current = byKind.get(document.kind);
    if (!current || new Date(document.effective_from).getTime() > new Date(current.effective_from).getTime()) {
      byKind.set(document.kind, document);
    }
  }
  return [...byKind.values()].sort((a, b) => a.kind.localeCompare(b.kind));
}

async function fetchActiveForLocale(
  client: LegalClient,
  locale: LegalLocale,
  kinds: readonly LegalDocumentKind[] | undefined,
  now: Date,
): Promise<LegalDocument[]> {
  let query = client
    .from('legal_documents')
    .select(DOCUMENT_COLUMNS)
    .eq('locale', locale)
    .lte('effective_from', now.toISOString());

  if (kinds) query = query.in('kind', kinds);

  const result = (await query) as QueryResult<unknown>;
  if (result.error) throw new LegalDataError('legal_documents_fetch_failed', { cause: result.error });

  return (result.data ?? [])
    .map(parseDocument)
    .filter((document) => isActive(document, now));
}

/**
 * Busca a revisão ativa de cada kind. O locale solicitado ganha prioridade; pt-PT só preenche um
 * kind que ainda não tem tradução publicada. A publicação continua exigindo os quatro locales,
 * portanto este fallback nunca é usado para validar cadastro ou publicar uma revisão.
 */
export async function getActiveDocuments(
  client: LegalClient,
  locale: string,
  kinds?: readonly LegalDocumentKind[],
  now = new Date(),
): Promise<LegalDocument[]> {
  assertLocale(locale);
  assertKinds(kinds);

  // As traduções não dependem umas das outras; evitar um RTT sequencial no gate privado.
  const [requested, fallback] = await Promise.all([
    fetchActiveForLocale(client, locale, kinds, now),
    locale === 'pt-PT' ? Promise.resolve([]) : fetchActiveForLocale(client, 'pt-PT', kinds, now),
  ]);

  const preferred = new Map(newestPerKind(requested).map((document) => [document.kind, document]));
  for (const document of newestPerKind(fallback)) {
    if (!preferred.has(document.kind)) preferred.set(document.kind, document);
  }

  return [...preferred.values()].sort((a, b) => a.kind.localeCompare(b.kind));
}

const REQUIRED_KINDS: Record<LegalRole, readonly LegalDocumentKind[]> = {
  consumer: ['terms_consumer', 'privacy_policy'],
  professional_owner: ['terms_business', 'privacy_policy'],
  professional_employee: ['terms_business', 'privacy_policy'],
  customer_success: ['terms_consumer', 'privacy_policy'],
  admin: ['terms_consumer', 'terms_business', 'privacy_policy'],
  system: ['terms_consumer', 'terms_business', 'privacy_policy'],
};

/**
 * Espelho do conjunto validado pelo `signup-guard`. T1.19b (ADR 0039): toda conta nasce consumer,
 * logo a Entrada B aceita os termos do consumidor além dos de negócio.
 */
const SIGNUP_REQUIRED_KINDS: Record<SignupRoleIntent, readonly LegalDocumentKind[]> = {
  consumer: ['terms_consumer', 'privacy_policy'],
  professional_owner: ['terms_consumer', 'terms_business', 'privacy_policy'],
};

/**
 * Resolve o conjunto jurídico exato exibido no cadastro. Diferentemente da leitura pública de um
 * documento, cadastro não admite fallback de locale: o titular precisa aceitar precisamente as
 * duas revisões que leu. Mais de uma revisão ativa da mesma kind também falha fechando, pois a UI
 * não deve escolher silenciosamente entre contratos concorrentes.
 */
export async function getRequiredSignupDocuments(
  client: LegalClient,
  locale: string,
  roleIntent: SignupRoleIntent,
  now = new Date(),
): Promise<LegalDocument[]> {
  assertLocale(locale);
  if (!Object.prototype.hasOwnProperty.call(SIGNUP_REQUIRED_KINDS, roleIntent)) {
    throw new LegalDataError('role_intent_invalido');
  }

  const requiredKinds = SIGNUP_REQUIRED_KINDS[roleIntent];
  const active = await fetchActiveForLocale(client, locale, requiredKinds, now);
  const byKind = new Map<LegalDocumentKind, LegalDocument[]>();

  for (const document of active) {
    const documents = byKind.get(document.kind) ?? [];
    documents.push(document);
    byKind.set(document.kind, documents);
  }

  if (requiredKinds.some((kind) => (byKind.get(kind)?.length ?? 0) > 1)) {
    throw new LegalDataError('legal_signup_documents_ambiguous');
  }
  if (requiredKinds.some((kind) => (byKind.get(kind)?.length ?? 0) !== 1)) {
    throw new LegalDataError('legal_signup_documents_incomplete');
  }

  return requiredKinds
    .map((kind) => byKind.get(kind)![0]!)
    .sort((a, b) => a.kind.localeCompare(b.kind));
}

function acceptanceKey(kind: string, version: string): string {
  return `${kind}\u0000${version}`;
}

/**
 * Devolve apenas revisões vigentes obrigatórias que ainda não constam da prova do titular.
 * Aceitar a mesma kind+versão em outro locale continua válido: o aceite está ligado à revisão
 * jurídica, e trocar a língua da interface não é uma alteração contratual.
 */
export async function getPendingReacceptance(
  client: LegalClient,
  userId: string,
  locale: string,
  role: LegalRole,
  now = new Date(),
): Promise<LegalDocument[]> {
  assertLocale(locale);
  if (typeof userId !== 'string' || userId.trim() === '') throw new LegalDataError('user_id_invalido');
  if (!Object.prototype.hasOwnProperty.call(REQUIRED_KINDS, role)) {
    throw new LegalDataError('role_invalido');
  }

  return pendingAmong(client, userId, locale, REQUIRED_KINDS[role], now);
}

/** Estado dos termos de negócio para o titular (T1.19b / D5, ADR 0039). */
export type BusinessTermsStatus =
  | { state: 'accepted' }
  | { state: 'pending'; document: LegalDocument }
  | { state: 'no_current_document' };

/**
 * Termos de negócio vigentes e se o titular já os aceitou — alimenta a caixa da etapa 1 do
 * onboarding profissional. Não depende do papel: no upgrade "Tornar-me profissional" o titular
 * ainda é `consumer` quando os aceita. Mesma regra de revisão do re-aceite (kind + versão), e mesmo
 * fallback de locale. Sem versão vigente publicada, não há o que aceitar.
 */
export async function getBusinessTermsStatus(
  client: LegalClient,
  userId: string,
  locale: string,
  now = new Date(),
): Promise<BusinessTermsStatus> {
  assertLocale(locale);
  if (typeof userId !== 'string' || userId.trim() === '') throw new LegalDataError('user_id_invalido');

  const active = await getActiveDocuments(client, locale, ['terms_business'], now);
  if (active.length === 0) return { state: 'no_current_document' };

  const [document] = await pendingAmong(client, userId, locale, ['terms_business'], now, active);
  return document ? { state: 'pending', document } : { state: 'accepted' };
}

/** Revisões vigentes das `kinds` que ainda não constam da prova do titular. */
async function pendingAmong(
  client: LegalClient,
  userId: string,
  locale: string,
  kinds: readonly LegalDocumentKind[],
  now: Date,
  preloaded?: LegalDocument[],
): Promise<LegalDocument[]> {
  // A prova de aceite e o catálogo vigente também são independentes. O guard só libera após ambos.
  const activePromise = preloaded ? Promise.resolve(preloaded) : getActiveDocuments(client, locale, kinds, now);
  const acceptancePromise = client
    .from('legal_acceptances')
    .select('document_id, legal_documents(kind, version, locale)')
    .eq('user_id', userId) as PromiseLike<QueryResult<LegalAcceptanceReference>>;
  const [active, result] = await Promise.all([activePromise, acceptancePromise]);

  if (result.error) throw new LegalDataError('legal_acceptances_fetch_failed', { cause: result.error });

  const accepted = new Set(
    (result.data ?? [])
      .map((row) => row.legal_documents)
      .filter((document): document is Pick<LegalDocument, 'kind' | 'version' | 'locale'> => Boolean(document))
      .map((document) => acceptanceKey(document.kind, document.version)),
  );

  return active.filter((document) => !accepted.has(acceptanceKey(document.kind, document.version)));
}

/**
 * Registra um aceite pela Edge Function. O client não recebe `user_id`, IP ou timestamp: esses
 * campos são derivados no servidor a partir da sessão e do request, preservando a auditabilidade.
 */
export async function acceptLegalDocument(
  client: LegalClient,
  documentId: string,
  method: LegalAcceptanceMethod,
): Promise<LegalAcceptance> {
  if (typeof documentId !== 'string' || documentId.trim() === '') {
    throw new LegalDataError('document_id_invalido');
  }
  if (!ACCEPTANCE_METHOD_SET.has(method)) throw new LegalDataError('method_invalido');
  if (!client.functions) throw new LegalDataError('legal_accept_unavailable');

  const result = await client.functions.invoke('legal-accept', {
    body: { document_id: documentId, method },
  });
  if (result.error) throw new LegalDataError('legal_accept_failed', { cause: result.error });

  return parseAcceptance(result.data);
}
